<template>
  <div class="card">
    <div class="card-body">
      <form @submit.prevent="submitPayment">
        <div class="row">
          <div class="col-xxl-6 col-sm-6 col-12">
            <div class="input-blocks">
              <label>Date</label>
              <div class="input-groupicon">
                <input
                  v-model="form.date"
                  type="date"
                  class="form-control"
                  :class="{ 'is-invalid': errors.date }"
                >
                <div v-if="errors.date" class="invalid-feedback">
                  {{ errors.date[0] }}
                </div>
              </div>
            </div>
          </div>

          <div class="col-xxl-6 col-sm-6 col-12">
            <div class="input-blocks">
              <label>Supplier Name</label>
              <div class="input-groupicon">
                <input
                  v-model="supplierData.name"
                  type="text"
                  readonly
                  class="form-control"
                >
              </div>
            </div>
          </div>

          <div class="col-xxl-6 col-sm-6 col-12">
            <div class="input-blocks">
              <label>Total Due Amount</label>
              <div class="input-groupicon">
                <input
                  type="text"
                  :value="formatCurrency(supplierData.total_due || 0)"
                  readonly
                  class="form-control"
                >
              </div>
            </div>
          </div>

          <div class="col-xxl-6 col-sm-6 col-12">
            <div class="input-blocks">
              <label>Payment Amount</label>
              <div class="input-groupicon">
                <input
                  v-model.number="form.payment_amount"
                  type="number"
                  class="form-control"
                  :class="{ 'is-invalid': errors.payment_amount }"
                  step="0.01"
                  min="0"
                  :max="supplierData.total_due || 0"
                >
                <div v-if="errors.payment_amount" class="invalid-feedback">
                  {{ errors.payment_amount[0] }}
                </div>
              </div>
            </div>
          </div>
        </div>

        <div class="row">
          <div class="col-12">
            <div class="input-blocks">
              <label>Remarks</label>
              <div class="input-groupicon">
                <textarea
                  v-model="form.notes"
                  class="form-control"
                  rows="3"
                ></textarea>
              </div>
            </div>
          </div>

          <div class="col-lg-12 text-end">
            <button type="button" class="btn btn-cancel me-3" @click="cancelPayment">
              Cancel
            </button>
            <button type="submit" class="btn btn-submit" :disabled="loading">
              <span v-if="loading" class="spinner-border spinner-border-sm me-2" role="status"></span>
              Submit
            </button>
          </div>
        </div>
      </form>
    </div>
  </div>
</template>

<script>
import { showSuccessMessage, showErrorMessage } from '../utils/notifications.js';

export default {
  name: 'SupplierPayment',

  props: {
    supplierId: {
      type: [String, Number],
      required: true
    }
  },

  data() {
    return {
      form: {
        date: new Date().toISOString().split('T')[0],
        payment_amount: 0,
        notes: ''
      },
      supplierData: {
        id: null,
        name: '',
        total_due: 0
      },
      invoiceNo: '',
      errors: {},
      loading: false,
      initialLoading: true
    };
  },

  async mounted() {
    console.log('SupplierPayment component mounted');
    console.log('Supplier ID:', this.supplierId);

    // Mark element as mounted
    this.$el.closest('[data-vue-component]')?.classList.add('vue-mounted');

    await this.loadSupplierData();
  },

  methods: {
    async loadSupplierData() {
      this.initialLoading = true;
      try {
        const response = await this.$http.get(`/api/suppliers/${this.supplierId}/payment-details`);

        if (response.data.success) {
          this.supplierData = response.data.supplier;
          this.invoiceNo = response.data.invoice_no;
          // Set default payment amount to total due
          this.form.payment_amount = this.supplierData.total_due;
        } else {
          this.showErrorMessage(response.data.message || 'Failed to load supplier data');
        }
      } catch (error) {
        console.error('Failed to load supplier data:', error);
        this.showErrorMessage('Failed to load supplier data');
      } finally {
        this.initialLoading = false;
      }
    },

    async submitPayment() {
      this.loading = true;
      this.errors = {};

      try {
        // Client-side validation
        if (this.form.payment_amount > this.supplierData.total_due) {
          this.errors.payment_amount = ['Payment amount cannot exceed total due'];
          this.loading = false;
          return;
        }

        if (this.form.payment_amount <= 0) {
          this.errors.payment_amount = ['Payment amount must be greater than 0'];
          this.loading = false;
          return;
        }

        const response = await this.$http.post(`/api/suppliers/${this.supplierId}/payment`, {
          date: this.form.date,
          payment_amount: this.form.payment_amount,
          notes: this.form.notes
        });

        if (response.data.success) {
          this.showSuccessMessage(response.data.message);

          // Redirect after a short delay
          setTimeout(() => {
            if (response.data.redirect_url) {
              window.location.href = response.data.redirect_url;
            } else {
              window.location.href = '/supplier/list';
            }
          }, 1500);
        } else {
          this.showErrorMessage(response.data.message || 'Payment processing failed');
        }

      } catch (error) {
        console.error('Payment submission failed:', error);

        if (error.response?.status === 422) {
          this.errors = error.response.data.errors || {};
          this.showErrorMessage('Please check the form for errors');
        } else {
          this.showErrorMessage(
            error.response?.data?.message || 'An error occurred while processing the payment'
          );
        }
      } finally {
        this.loading = false;
      }
    },

    cancelPayment() {
      window.location.href = '/supplier/list';
    },

    formatCurrency(amount) {
      return new Intl.NumberFormat('en-US', {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2
      }).format(amount || 0);
    },

    showSuccessMessage,
    showErrorMessage
  }
};
</script>

<style scoped>
.spinner-border-sm {
  width: 1rem;
  height: 1rem;
}

.vue-component-loading {
  display: none;
}
</style>
